import { useQuery, useQueryClient, type QueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import {
  mergeQueryOptions,
  retryPageReadRequirement,
  type QueryOptions,
} from '@/modules/query/utils';
import { isAuthenticatedByRLRef } from '@/modules/auth/state';
import { CURRENT_USER_PERMISSIONS_QUERY } from './queryKeys';

// This is NOT the permissions enum as strings can contain wildcards
// Endpoint return type has changed to be object with permissions and accessibleSuppliers not just string[]
export type UserPermissionsResponse =
  | string[]
  | { permissions: string[]; accessibleSuppliers: string[] };

async function fetchUserPermissions() {
  const res = await request<UserPermissionsResponse>('GET', '/user/permissions');
  return res.data;
}

// Flatten the two response shapes to a plain permission list. Shared so callers
// that read straight from the query cache (e.g. mode resolution during an org
// switch) normalise identically to the reactive `permissions` ref below.
export function normalizeUserPermissions(data: UserPermissionsResponse | undefined): string[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.permissions;
}

// Imperatively (re)fetch the user's permissions into the cache. Used by the org
// soft reload, where the app shell is unmounted behind the loading screen so no
// observer is alive to refetch after the cache is cleared.
export function fetchCurrentUserPermissions(queryClient: QueryClient) {
  return queryClient.fetchQuery({
    queryKey: [CURRENT_USER_PERMISSIONS_QUERY],
    queryFn: fetchUserPermissions,
  });
}

export function useCurrentUserPermissions(options?: QueryOptions) {
  const queryClient = useQueryClient();

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CURRENT_USER_PERMISSIONS_QUERY],
      queryFn: fetchUserPermissions,
      staleTime: Infinity,
      enabled: isAuthenticatedByRLRef,
      ...retryPageReadRequirement(),
    }),
  );

  const permissions = computed<string[]>(() => normalizeUserPermissions(query.data.value));

  const accessibleSuppliers = computed(() => {
    if (!query.data.value || Array.isArray(query.data.value)) return [];
    return query.data.value.accessibleSuppliers;
  });

  function override(newPermissions: string[]) {
    queryClient.setQueryData([CURRENT_USER_PERMISSIONS_QUERY], newPermissions);
  }

  return { ...query, permissions, override, accessibleSuppliers };
}
